/**
 * Client-side checks that uploaded files are readable and match their type
 * (magic bytes). Backend still re-validates — this is UX only.
 */

export type ReadableUploadKind = 'pdf' | 'image' | 'pdf-or-image';

export type ReadableUploadResult =
  | { ok: true }
  | { ok: false; errorKey: string };

const PDF_HEADER = [0x25, 0x50, 0x44, 0x46, 0x2d]; // %PDF-
const PNG_HEADER = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const JPEG_SOI = [0xff, 0xd8, 0xff];

function bytesMatch(buf: Uint8Array, expected: number[], offset = 0): boolean {
  if (buf.length < offset + expected.length) {
    return false;
  }
  for (let i = 0; i < expected.length; i++) {
    if (buf[offset + i] !== expected[i]) {
      return false;
    }
  }
  return true;
}

function extensionOf(file: File): string {
  const name = (file.name || '').toLowerCase();
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1) : '';
}

function allowedExtensions(kind: ReadableUploadKind): string[] {
  if (kind === 'pdf') {
    return ['pdf'];
  }
  if (kind === 'image') {
    return ['jpg', 'jpeg', 'png'];
  }
  return ['pdf', 'jpg', 'jpeg', 'png'];
}

function sniffKind(header: Uint8Array): 'pdf' | 'jpeg' | 'png' | null {
  if (bytesMatch(header, PDF_HEADER)) {
    return 'pdf';
  }
  if (bytesMatch(header, PNG_HEADER)) {
    return 'png';
  }
  if (bytesMatch(header, JPEG_SOI)) {
    return 'jpeg';
  }
  return null;
}

async function readHeader(file: File, length = 16): Promise<Uint8Array> {
  const blob = file.slice(0, length);
  const buf = await blob.arrayBuffer();
  return new Uint8Array(buf);
}

async function canDecodeImage(file: File): Promise<boolean> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bmp = await createImageBitmap(file);
      bmp.close();
      return true;
    } catch {
      return false;
    }
  }
  return new Promise(resolve => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(true);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(false);
    };
    img.src = url;
  });
}

async function looksLikePdf(file: File): Promise<boolean> {
  const head = await readHeader(file, 5);
  if (!bytesMatch(head, PDF_HEADER)) {
    return false;
  }
  // Soft EOF check near end
  const tailSize = Math.min(2048, file.size);
  if (tailSize <= 0) {
    return false;
  }
  const tail = new Uint8Array(
    await file.slice(file.size - tailSize, file.size).arrayBuffer()
  );
  const text = Array.from(tail)
    .map(b => (b >= 32 && b < 127 ? String.fromCharCode(b) : ' '))
    .join('');
  return text.indexOf('%%EOF') !== -1;
}

/**
 * Validate extension, size, magic bytes and that the file can be read.
 */
export async function assertReadableUploadFile(
  file: File,
  options: {
    kind?: ReadableUploadKind;
    maxMb?: number;
  } = {}
): Promise<ReadableUploadResult> {
  const kind = options.kind || 'pdf-or-image';
  const maxMb = options.maxMb != null ? options.maxMb : 10;

  if (!file || file.size <= 0) {
    return { ok: false, errorKey: 'FILE_CORRUPT_OR_UNREADABLE' };
  }

  if (file.size / (1024 * 1024) > maxMb) {
    return { ok: false, errorKey: 'FILE_SIZE_EXCEEDED' };
  }

  const ext = extensionOf(file);
  if (allowedExtensions(kind).indexOf(ext) === -1) {
    return { ok: false, errorKey: 'FILE_TYPE_NOT_ALLOWED' };
  }

  let header: Uint8Array;
  try {
    header = await readHeader(file);
  } catch {
    return { ok: false, errorKey: 'FILE_CORRUPT_OR_UNREADABLE' };
  }

  const sniffed = sniffKind(header);
  if (!sniffed) {
    return { ok: false, errorKey: 'FILE_CORRUPT_OR_UNREADABLE' };
  }

  if (kind === 'pdf' && sniffed !== 'pdf') {
    return { ok: false, errorKey: 'FILE_TYPE_NOT_ALLOWED' };
  }
  if (kind === 'image' && sniffed === 'pdf') {
    return { ok: false, errorKey: 'FILE_TYPE_NOT_ALLOWED' };
  }

  if (sniffed === 'pdf' && (ext !== 'pdf')) {
    return { ok: false, errorKey: 'FILE_CONTENT_MISMATCH' };
  }
  if (sniffed === 'jpeg' && ext !== 'jpg' && ext !== 'jpeg') {
    return { ok: false, errorKey: 'FILE_CONTENT_MISMATCH' };
  }
  if (sniffed === 'png' && ext !== 'png') {
    return { ok: false, errorKey: 'FILE_CONTENT_MISMATCH' };
  }

  if (sniffed === 'pdf') {
    const okPdf = await looksLikePdf(file);
    if (!okPdf) {
      return { ok: false, errorKey: 'FILE_CORRUPT_OR_UNREADABLE' };
    }
    return { ok: true };
  }

  const okImg = await canDecodeImage(file);
  if (!okImg) {
    return { ok: false, errorKey: 'FILE_CORRUPT_OR_UNREADABLE' };
  }
  return { ok: true };
}
